import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  FiUpload,
  FiFile,
  FiImage,
  FiMusic,
  FiVideo,
  FiTrash2,
} from "react-icons/fi";
interface UploadedFile extends File {
  preview?: string | null;
}

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  maxFiles?: number;
  disabled?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  maxFiles = 1,
  disabled = false,
}) => {
  const [file, setFile] = useState<UploadedFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles?.length) {
        const newFile = Object.assign(acceptedFiles[0], {
          preview: acceptedFiles[0].type.startsWith("image/")
            ? URL.createObjectURL(acceptedFiles[0])
            : null,
        });
        setFile(newFile);
        onFileSelect(newFile);
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif"],
      "video/*": [".mp4", ".mov"],
      "audio/*": [".mp3", ".wav"],
      "application/pdf": [".pdf"],
    },
    maxSize: maxFileSize,
    maxFiles,
    disabled,
  });

  const removeFile = () => {
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
    }
    setFile(null);
    onFileSelect(null);
    setUploadProgress(0);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) return <FiImage className="fs-4" />;
    if (fileType.startsWith("video/")) return <FiVideo className="fs-4" />;
    if (fileType.startsWith("audio/")) return <FiMusic className="fs-4" />;
    return <FiFile className="fs-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  useEffect(() => {
    return () => {
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
    };
  }, [file]);

  return (
    <div className="container">
      <div
        {...getRootProps()}
        className={`border border-dashed p-3 text-center rounded ${
          isDragActive ? "border-primary bg-light" : "border-secondary"
        } ${disabled ? "bg-light" : ""}`}
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      >
        <input {...getInputProps()} />
        <FiUpload className="fs-1 text-secondary" />
        <p className="mt-2 mb-1">Drag and drop file here or click to browse</p>
        <p className="text-muted small">
          Maximum file size: {formatFileSize(maxFileSize)} • {maxFiles} file
          allowed
        </p>
      </div>

      {file && (
        <div className="mt-3">
          <div className="card shadow-sm">
            <div className="card-body d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                {file.preview ? (
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="rounded me-3"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  getFileIcon(file.type)
                )}
                <div>
                  <h6
                    className="mb-1 text-truncate"
                    style={{ maxWidth: "200px" }}
                  >
                    {file.name}
                  </h6>
                  <p className="mb-0 text-muted small">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
              <button
                onClick={removeFile}
                className="btn btn-sm btn-outline-danger"
                disabled={uploading}
              >
                <FiTrash2 />
              </button>
            </div>
            {uploading && (
              <div className="progress mt-2">
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  {uploadProgress}%
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
